import { FirebaseError } from 'firebase/app';

/**
 * Retry configuration options
 */
export interface RetryConfig {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
}

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2
};

/**
 * Checks if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  if (!(error instanceof FirebaseError)) {
    return false;
  }

  const retryableCodes = [
    'unavailable',
    'deadline-exceeded',
    'resource-exhausted',
    'internal',
    'unknown',
    'failed-precondition'
  ];

  return retryableCodes.includes(error.code);
}

/**
 * Delays execution for a specified time
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calculates the delay for the next retry with exponential backoff
 */
function calculateBackoff(
  attempt: number,
  config: Required<RetryConfig>
): number {
  const exponentialDelay = config.initialDelay * Math.pow(config.backoffMultiplier, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // Add 0-30% jitter
  return Math.min(exponentialDelay + jitter, config.maxDelay);
}

/**
 * Retries a Firebase operation with exponential backoff
 * 
 * @param operation - The async operation to retry
 * @param config - Retry configuration options
 * @returns The result of the operation
 * @throws The last error if all retries fail
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const finalConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: unknown;

  for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      // Attempt the operation
      return await operation();
    } catch (error) {
      lastError = error;

      // Don't retry if it's not a retryable error
      if (!isRetryableError(error)) {
        throw error;
      }

      // Don't retry if we've exhausted all attempts
      if (attempt === finalConfig.maxRetries) {
        // Silently fail - no console noise
        throw error;
      }

      // Calculate backoff delay and wait before retrying (silently)
      const backoffDelay = calculateBackoff(attempt, finalConfig);
      await delay(backoffDelay);
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError;
}

/**
 * Wraps a Firebase query/operation with retry logic
 * 
 * Example usage:
 * ```typescript
 * const data = await withRetry(() => getDoc(docRef));
 * ```
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  customConfig?: RetryConfig
): Promise<T> {
  return retryOperation(operation, customConfig);
}

/**
 * Creates a retry-enabled version of a function
 * 
 * Example usage:
 * ```typescript
 * const getUserWithRetry = createRetryableFunction(getUser);
 * const user = await getUserWithRetry(userId);
 * ```
 */
export function createRetryableFunction<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  config?: RetryConfig
) {
  return (...args: TArgs): Promise<TReturn> => {
    return retryOperation(() => fn(...args), config);
  };
}
